// Copyright 2026. WebPros International GmbH. All rights reserved.

import { IComputeResourceResponse } from 'common/api/resources/ComputeResource/model';
import { COMPUTE_RESOURCE_HA_STATUS } from 'admin/computeResource/constants';
import {
    ISankeyLinkInput,
    ISankeyNodeInput,
} from 'admin/failoverDomain/page/components/SummaryCards/useSankeyLayout';
import { GiB } from 'common/helpers/units';
import { getComputeResourceRamCapacity } from 'common/helpers/computeResource';

// ── HA status display metadata ────────────────────────────────────────────────

const HA_STATUS_COLORS: Record<COMPUTE_RESOURCE_HA_STATUS, string> = {
    [COMPUTE_RESOURCE_HA_STATUS.PROTECTED]: '#709B1D',
    [COMPUTE_RESOURCE_HA_STATUS.AT_RISK]: '#D66B0E',
    [COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE]: '#737373',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_ELIGIBLE]: '#b5b5b5',
    [COMPUTE_RESOURCE_HA_STATUS.ELIGIBLE]: '#28AADE',
    [COMPUTE_RESOURCE_HA_STATUS.PAUSED]: '#9e9e9e',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_PROTECTED]: '#BE2D45',
};

const HA_STATUS_LABELS: Record<COMPUTE_RESOURCE_HA_STATUS, string> = {
    [COMPUTE_RESOURCE_HA_STATUS.PROTECTED]: 'OK',
    [COMPUTE_RESOURCE_HA_STATUS.AT_RISK]: 'At risk',
    [COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE]: 'Unavailable',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_ELIGIBLE]: 'Not eligible',
    [COMPUTE_RESOURCE_HA_STATUS.ELIGIBLE]: 'Eligible',
    [COMPUTE_RESOURCE_HA_STATUS.PAUSED]: 'Paused',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_PROTECTED]: 'Not protected',
};

// Statuses that indicate a CR is not actively participating in HA.
const INACTIVE_HA_STATUSES = new Set<COMPUTE_RESOURCE_HA_STATUS>([
    COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE,
    COMPUTE_RESOURCE_HA_STATUS.PAUSED,
]);

// ── Unit helper ───────────────────────────────────────────────────────────────

const bytesToGiB = (bytes: number) => Math.round(bytes / GiB);

// ── Return type ───────────────────────────────────────────────────────────────

export interface IFdSankeyData {
    nodes: ISankeyNodeInput[];
    links: ISankeyLinkInput[];
    totalRamGiB: number;
    formatValue: (bytes: number) => string;
}

// ── Builder ───────────────────────────────────────────────────────────────────

/**
 * Derives Sankey nodes and links from the list of compute resources on a
 * Failover Domain. Produces a 4-column flow (all values in MiB):
 *
 *   Col 0 — single "N in total" node
 *   Col 1 — one node per HA status, sized by total RAM
 *   Col 2 — online RAM vs offline RAM
 *   Col 3 — HA-allocated RAM / non-HA RAM / free RAM
 */
export const buildFdSankeyData = (computeResources: IComputeResourceResponse[]): IFdSankeyData => {
    const formatValue = (bytes: number) => `${bytesToGiB(bytes)} GiB`;

    if (computeResources.length === 0) {
        return { nodes: [], links: [], totalRamGiB: 0, formatValue };
    }

    // ── Aggregate per HA status ───────────────────────────────────────────────

    const haStatusGroups = new Map<COMPUTE_RESOURCE_HA_STATUS, { count: number; ram: number }>();
    let onlineRam = 0;
    let offlineRam = 0;
    let onlineUsedRam = 0;
    let haAllocatedRam = 0;

    for (const cr of computeResources) {
        const haStatus = cr.ha_readiness_status;
        if (!haStatus) {
            continue;
        }

        // Capacity is reported as-is: a CR that has never reported host memory and has no RAM
        // limit contributes 0 rather than a placeholder, so the diagram never overstates the
        // domain. An offline CR keeps contributing its last known host memory, because
        // `metrics` is persisted server-side and is not cleared when the CR goes away.
        const effectiveRam = getComputeResourceRamCapacity(cr);
        const usedRam = cr.settings.limits.ram.used ?? 0;

        const group = haStatusGroups.get(haStatus) ?? { count: 0, ram: 0 };
        group.count += 1;
        group.ram += effectiveRam;
        haStatusGroups.set(haStatus, group);

        if (INACTIVE_HA_STATUSES.has(haStatus)) {
            offlineRam += effectiveRam;
        } else {
            onlineRam += effectiveRam;
            onlineUsedRam += usedRam;
            if (cr.vms_count) {
                const haFraction = (cr.high_availability_enabled_servers_count ?? 0) / cr.vms_count;
                haAllocatedRam += usedRam * haFraction;
            }
        }
    }

    const totalRam = onlineRam + offlineRam;
    if (totalRam === 0) {
        return { nodes: [], links: [], totalRamGiB: 0, formatValue };
    }

    // ── Derived values ────────────────────────────────────────────────────────

    // Usage is reported by the CRs independently of the capacity above, so it can exceed it — a
    // RAM limit set below current allocations, or a CR that reports allocations before it
    // reports host memory. Cap the allocated total, then split it in the proportion the CRs
    // actually reported: taking the excess out of the non-HA share alone would erase that band
    // and claim the whole allocation is HA-protected. The two `Math.min` calls are the only
    // guards needed — they leave `nonHaRam` and `onlineFreeRam` non-negative by construction,
    // and the three col-3 values summing to `onlineRam` is what keeps the diagram conserving.
    const allocatedRam = Math.min(onlineUsedRam, onlineRam);
    const haShareOfUsage = onlineUsedRam > 0 ? haAllocatedRam / onlineUsedRam : 0;
    const haRam = Math.min(Math.round(allocatedRam * haShareOfUsage), allocatedRam);
    const nonHaRam = allocatedRam - haRam;
    const onlineFreeRam = onlineRam - allocatedRam;
    const freeRam = onlineFreeRam + offlineRam;

    // ── Build nodes and links ─────────────────────────────────────────────────

    const nodes: ISankeyNodeInput[] = [];
    const links: ISankeyLinkInput[] = [];

    // Col 0 — aggregate total
    nodes.push({
        id: 'total',
        label: `${computeResources.length} in total`,
        color: '#1C6B90',
    });

    // Col 1 — one node per HA status; Col 1→2 links built in the same pass
    for (const [haStatus, group] of Array.from(haStatusGroups.entries())) {
        if (group.ram === 0) {
            continue;
        }
        nodes.push({
            id: `status_${haStatus}`,
            label: `${group.count} - ${HA_STATUS_LABELS[haStatus]}`,
            color: HA_STATUS_COLORS[haStatus],
        });
        links.push({ sourceId: 'total', targetId: `status_${haStatus}`, value: group.ram });
        const col2Target = INACTIVE_HA_STATUSES.has(haStatus) ? 'offline' : 'online';
        links.push({ sourceId: `status_${haStatus}`, targetId: col2Target, value: group.ram });
    }

    // Col 2 — online vs offline
    if (onlineRam > 0) {
        nodes.push({ id: 'online', label: `${bytesToGiB(onlineRam)} GiB online`, color: '#709B1D' });
    }
    if (offlineRam > 0) {
        nodes.push({ id: 'offline', label: `${bytesToGiB(offlineRam)} GiB offline`, color: '#BE2D45' });
    }

    // Col 3 — HA / non-HA / free breakdown
    if (haRam > 0) {
        nodes.push({ id: 'ha', label: `${bytesToGiB(haRam)} GiB - HA servers`, color: '#709B1D' });
        links.push({ sourceId: 'online', targetId: 'ha', value: haRam });
    }
    if (nonHaRam > 0) {
        nodes.push({ id: 'non_ha', label: `${bytesToGiB(nonHaRam)} GiB - non-HA servers`, color: '#28AADE' });
        links.push({ sourceId: 'online', targetId: 'non_ha', value: nonHaRam });
    }
    if (freeRam > 0) {
        nodes.push({ id: 'free', label: `${bytesToGiB(freeRam)} GiB - free`, color: '#737373' });
        if (onlineFreeRam > 0) {
            links.push({ sourceId: 'online', targetId: 'free', value: onlineFreeRam });
        }
        if (offlineRam > 0) {
            links.push({ sourceId: 'offline', targetId: 'free', value: offlineRam, color: '#BE2D45' });
        }
    }

    return { nodes, links, totalRamGiB: bytesToGiB(totalRam), formatValue };
};
